import React, { useState } from 'react';
import { Box, CircularProgress, IconButton, Switch, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
  useNotificationPreferencesQuery,
  useUpdateNotificationPreferenceMutation,
} from '../dataHandlers/notificationPreferencesHandlers';

const EmailNotificationPreferencesSection = () => {
  const [documentApproval, setDocumentApproval] = useState(false);
  const [documentRejection, setDocumentRejection] = useState(false);
  const [updatingPreference, setUpdatingPreference] = useState(null);

  const { notificationPreferences, isLoading } = useNotificationPreferencesQuery();

  // Sync the fetched preferences with the component state.
  React.useEffect(() => {
    if (!notificationPreferences) return;

    setDocumentApproval(notificationPreferences.DocumentApproval ?? false);

    setDocumentRejection(notificationPreferences.DocumentRejection ?? false);
  }, [notificationPreferences]);

  const { onSubmit, isPending } = useUpdateNotificationPreferenceMutation({
    onError: (error, variables) => {
      // Revert the optimistic update.
      const previousValue = !variables.value;

      if (variables.key === 'receive_on_document_approval') {
        setDocumentApproval(previousValue);
      } else if (variables.key === 'receive_on_document_rejection') {
        setDocumentRejection(previousValue);
      }

      setUpdatingPreference(null);
    },

    onSuccess: (data, variables) => {
      // Update with the server response when available.
      if (variables.key === 'receive_on_document_approval' && typeof data?.DocumentApproval === 'boolean') {
        setDocumentApproval(data.DocumentApproval);
      }

      if (variables.key === 'receive_on_document_rejection' && typeof data?.DocumentRejection === 'boolean') {
        setDocumentRejection(data.DocumentRejection);
      }

      setUpdatingPreference(null);
    },
  });

  const handleToggle = (preference, currentValue) => {
    const newValue = !currentValue;

    // Optimistic update.
    if (preference === 'receive_on_document_approval') {
      setDocumentApproval(newValue);
    } else {
      setDocumentRejection(newValue);
    }

    setUpdatingPreference(preference);

    onSubmit(preference, newValue);
  };

  const isUpdating = isPending || updatingPreference !== null;

  return (
    <Box sx={{ mt: '2rem' }}>
      {/* Section title */}
      <Box
        sx={{
          mb: '1rem',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      />

      <Box sx={{ mb: '1rem' }}>
        <Typography variant="h6">Email Notification Preferences</Typography>
      </Box>

      {/* Notification settings table */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '38px',
            px: '0.75rem',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
            }}
          >
            SETTING
          </Typography>

          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
              mr: '0.75rem',
            }}
          >
            EMAIL
          </Typography>
        </Box>

        {/* Approval row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '42px',
            px: '0.75rem',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.15rem',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 300,
              }}
            >
              E-Document Approvals
            </Typography>

            <Tooltip title="Receive email notifications when an outbound invoice or credit note is successfully approved.">
              <IconButton size="small" sx={{ p: '2px' }}>
                <InfoOutlinedIcon
                  sx={{
                    fontSize: '14px',
                    color: 'text.secondary',
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>

          {isLoading ? (
            <CircularProgress size={18} sx={{ mr: '0.75rem' }} />
          ) : (
            <Switch
              checked={documentApproval}
              disabled={isUpdating}
              onChange={() => handleToggle('receive_on_document_approval', documentApproval)}
              size="small"
              sx={{ mr: '0.75rem' }}
            />
          )}
        </Box>

        {/* Rejection row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '42px',
            px: '0.75rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.15rem',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 300,
              }}
            >
              E-Documents Rejections & Validation Errors
            </Typography>
            <Tooltip title="Receive immediate email notifications when an outbound document fails schema validation or is rejected by C3 or C5.">
              <IconButton size="small" sx={{ p: '2px' }}>
                <InfoOutlinedIcon
                  sx={{
                    fontSize: '14px',
                    color: 'text.secondary',
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>

          {isLoading ? (
            <CircularProgress size={18} sx={{ mr: '0.75rem' }} />
          ) : (
            <Switch
              checked={documentRejection}
              disabled={isUpdating}
              onChange={() => handleToggle('receive_on_document_rejection', documentRejection)}
              size="small"
              sx={{ mr: '0.75rem' }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EmailNotificationPreferencesSection;
